import { keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import { Tooltip as RadixTooltip } from "radix-ui";
import type React from "react";
import type { ReactNode } from "react";

import { theme } from "@themes/themes";

type TooltipProps = {
  text: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
};

export const Tooltip: React.FC<TooltipProps> = ({ text, children, side = "top" }) => {
  const container = document.getElementById("portal-root")!;

  return (
    <RadixTooltip.Provider delayDuration={300} skipDelayDuration={0}>
      <RadixTooltip.Root>
        <RadixTooltip.Trigger asChild>{children}</RadixTooltip.Trigger>
        <RadixTooltip.Portal container={container}>
          <Content side={side} sideOffset={5}>
            {text}
            <RadixTooltip.Arrow asChild>
              <Arrow height={5} width={10} />
            </RadixTooltip.Arrow>
          </Content>
        </RadixTooltip.Portal>
      </RadixTooltip.Root>
    </RadixTooltip.Provider>
  );
};

const slideUpAndFade = keyframes`
  from { opacity: 0; transform: translateY(2px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideRightAndFade = keyframes`
  from { opacity: 0; transform: translateX(-2px); }
  to { opacity: 1; transform: translateX(0); }
`;
const slideDownAndFade = keyframes`
  from { opacity: 0; transform: translateY(-2px); }
  to { opacity: 1; transform: translateY(0); }
`;
const slideLeftAndFade = keyframes`
  from { opacity: 0; transform: translateX(2px); }
  to { opacity: 1; transform: translateX(0); }
`;

const Content = styled(RadixTooltip.Content)`
  border: 1px solid ${theme.color("gray-5")};

  border-radius: 4px;
  padding: 11px 8px;
  font-size: 15px;
  line-height: 1;
  color: white;
  background-color: ${theme.color("gray-4")};
  max-width: 300px;
  box-shadow:
    hsl(206 22% 7% / 35%) 0px 10px 38px -10px,
    hsl(206 22% 7% / 20%) 0px 10px 20px -15px;
  user-select: none;
  animation-duration: 400ms;
  animation-timing-function: cubic-bezier(0.16, 1, 0.3, 1);
  will-change: transform, opacity;

  &[data-state="delayed-open"][data-side="top"] {
    animation-name: ${slideDownAndFade};
  }
  &[data-state="delayed-open"][data-side="right"] {
    animation-name: ${slideLeftAndFade};
  }
  &[data-state="delayed-open"][data-side="bottom"] {
    animation-name: ${slideUpAndFade};
  }
  &[data-state="delayed-open"][data-side="left"] {
    animation-name: ${slideRightAndFade};
  }

  z-index: 1000;
`;

const Arrow = styled.svg`
  fill: ${theme.color("gray-2")};
`;
